import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { runKeyHandler } from '@material-ui/pickers/_shared/hooks/useKeyDown';
import { TimeBorder as TimeBorderIcon } from '@ringcentral/juno-icon';
import { combineClasses, combineProps, styled, useDepsChange, useEventCallback, useThemeProps, } from '../../../../foundation';
import { RcBox } from '../../../Box';
import { PickerTextField, } from '../utils';
import { TIME_SYSTEM_TEXT } from './constant';
import { NumberPicker, } from './NumberPicker';
import { SelectionView } from './SelectionView';
import { StyledPickerPopperWrap } from './styles';
import { ToggleText } from './ToggleText';
import { getFormattedTime, getHourAndMinute, getNumberPickerBoundary, getPeriod, getRangeBoundary, getRecoupHour, getTimestamp, getTimestampFromDate, HALF_DAY_HOURS, minuteSource, parseNumberToString, RcTimePickerClasses, TIME_GAP, twelveHourSystemSource, twentyFourHourSystemSource, } from './utils';
var defaultPeriodTexts = {
    input: { AM: 'AM', PM: 'PM' },
    toggle: { AM: 'AM', PM: 'PM' },
};
var _RcTimePicker = forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcTimePicker' });
    var isTwelveHourSystem = props.isTwelveHourSystem, value = props.value, onChange = props.onChange, disabled = props.disabled, dateMode = props.dateMode, min = props.min, max = props.max, size = props.size, onKeyDown = props.onKeyDown, onClear = props.onClear, HourPickerProps = props.HourPickerProps, MinutePickerProps = props.MinutePickerProps, PeriodToggleProps = props.PeriodToggleProps, PopoverPropsProp = props.PopoverProps, InputPropsProp = props.InputProps, classes = props.classes, defaultPickerValue = props.defaultPickerValue, _a = props.periodTexts, periodTexts = _a === void 0 ? defaultPeriodTexts : _a, rest = __rest(props, ["isTwelveHourSystem", "value", "onChange", "disabled", "dateMode", "min", "max", "size", "onKeyDown", "onClear", "HourPickerProps", "MinutePickerProps", "PeriodToggleProps", "PopoverProps", "InputProps", "classes", "defaultPickerValue", "periodTexts"]);
    var actionRef = useRef(null);
    var hourRef = useRef(null);
    var minuteRef = useRef(null);
    var periodRef = useRef(null);
    var textFiledValueRef = useRef('');
    var _b = __read(useState('none'), 2), selectionShowType = _b[0], setSelectionType = _b[1];
    var _c = (function () {
        if (value !== null)
            return { nowTime: value, isShowTextfieldValue: true };
        if (defaultPickerValue)
            return { nowTime: defaultPickerValue, isShowTextfieldValue: false };
        return { nowTime: null, isShowTextfieldValue: false };
    })(), nowTime = _c.nowTime, isShowTextfieldValue = _c.isShowTextfieldValue;
    var isHaveValue = nowTime !== null;
    var isDateMode = nowTime instanceof Date || dateMode;
    var currentTimestamp = getTimestamp(nowTime);
    var currentHourMinute = getHourAndMinute(currentTimestamp);
    var isHourView = selectionShowType === 'hour';
    var currentPeriod = getPeriod(currentHourMinute.hour);
    var recoupHour = isTwelveHourSystem && currentPeriod === TIME_SYSTEM_TEXT.PM
        ? HALF_DAY_HOURS
        : 0;
    var range = useMemo(function () {
        return getRangeBoundary({ min: min, max: max });
    }, [max, min]);
    var getEmitInitDate = useEventCallback(function () {
        var date = nowTime !== null ? new Date(nowTime) : new Date();
        var hour = currentHourMinute.hour, minute = currentHourMinute.minute;
        date.setHours(hour, minute, 0, 0);
        return date;
    });
    var handleChange = useCallback(function (toValue) {
        if (onChange) {
            if (isDateMode) {
                onChange(toValue);
            }
            else {
                // * when time is zero emit null
                onChange(toValue ? getTimestampFromDate(toValue) : null);
            }
        }
    }, [isDateMode, onChange]);
    var updateTime = function (option) {
        var _a, _b, _c;
        var _d = option.hour, hour = _d === void 0 ? (_a = hourRef.current) === null || _a === void 0 ? void 0 : _a.value : _d, _e = option.minute, minute = _e === void 0 ? (_b = minuteRef.current) === null || _b === void 0 ? void 0 : _b.value : _e, _f = option.period, period = _f === void 0 ? (_c = periodRef.current) === null || _c === void 0 ? void 0 : _c.value : _f;
        var time = getEmitInitDate();
        if (minute !== undefined) {
            time.setMinutes(minute);
        }
        if (hour !== undefined) {
            time.setHours(hour + recoupHour);
        }
        if (period !== undefined) {
            var currHour = time.getHours();
            if (period === TIME_SYSTEM_TEXT.PM) {
                if (currHour < HALF_DAY_HOURS) {
                    time.setHours(currHour + HALF_DAY_HOURS);
                }
            }
            else if (currHour >= HALF_DAY_HOURS) {
                time.setHours(currHour - HALF_DAY_HOURS);
            }
        }
        var toTimestamp = getTimestamp(time);
        if (range.max.date && toTimestamp > range.max.timestamp) {
            time.setHours(range.max.hour);
            time.setMinutes(range.max.minute);
        }
        else if (range.min.date && toTimestamp < range.min.timestamp) {
            time.setHours(range.min.hour);
            time.setMinutes(range.min.minute);
        }
        handleChange(time);
    };
    var handleHourChange = useEventCallback(function (hour) {
        updateTime({ hour: hour });
    });
    var handleMinuteChange = useEventCallback(function (minute) {
        return updateTime({ minute: minute });
    });
    var onTogglePeriod = useEventCallback(function (period) {
        return updateTime({ period: period });
    });
    var handleClear = useEventCallback(function (e) {
        onClear === null || onClear === void 0 ? void 0 : onClear(e);
        handleChange(null);
    });
    var handleSelectionViewClick = useEventCallback(function (toValue) {
        updateTime(isHourView ? { hour: toValue } : { minute: toValue });
        setSelectionType('none');
    });
    var closeMenu = useEventCallback(function () {
        var _a;
        (_a = actionRef.current) === null || _a === void 0 ? void 0 : _a.close();
    });
    var setHourSelectionShow = useEventCallback(function () {
        return setSelectionType('hour');
    });
    var setMinuteSelectionShow = useEventCallback(function () {
        return setSelectionType('minute');
    });
    var handlePopoverKeydown = useEventCallback(function (event) {
        var onConfirmAndClose = function () {
            updateTime({});
            closeMenu();
        };
        runKeyHandler(event, {
            Enter: onConfirmAndClose,
            ' ': onConfirmAndClose,
            Escape: closeMenu,
        });
    });
    var handleHourInnerChange = useEventCallback(function (hour) {
        var minuteInstance = minuteRef.current;
        var periodInstance = periodRef.current;
        var currRecoupHour = getRecoupHour(hour, periodInstance === null || periodInstance === void 0 ? void 0 : periodInstance.value);
        var newRange = getNumberPickerBoundary(hour + currRecoupHour, {
            range: range,
            isTwelveHourSystem: isTwelveHourSystem,
        });
        minuteInstance.setRange(newRange.minute);
    });
    var handlePeriodInnerChange = useEventCallback(function (period) {
        var minuteInstance = minuteRef.current;
        var hourInstance = hourRef.current;
        var currHour = hourInstance.value;
        var currRecoupHour = getRecoupHour(currHour, period);
        var newRange = getNumberPickerBoundary(currHour + currRecoupHour, {
            range: range,
            isTwelveHourSystem: isTwelveHourSystem,
        });
        hourInstance.setRange(newRange.hour);
        minuteInstance.setRange(newRange.minute);
    });
    var renderHourValue = useEventCallback(function (hourValue) {
        return parseNumberToString(hourValue, isTwelveHourSystem);
    });
    var PopoverProps = useMemo(function () {
        return combineProps({
            classes: combineClasses({
                root: RcTimePickerClasses.popover,
                paper: RcTimePickerClasses.popoverPaper,
            }, {
                root: classes === null || classes === void 0 ? void 0 : classes.popover,
                paper: classes === null || classes === void 0 ? void 0 : classes.popoverPaper,
            }),
            onKeyDown: handlePopoverKeydown,
            TransitionProps: {
                onEnter: function () { return setSelectionType('none'); },
            },
        }, PopoverPropsProp);
    }, [PopoverPropsProp, classes, handlePopoverKeydown]);
    // * make sure value inside range
    useLayoutEffect(function () {
        if (currentTimestamp === null)
            return;
        var time = getEmitInitDate();
        if (range.max.date && currentTimestamp > range.max.timestamp) {
            time.setHours(range.max.hour);
            time.setMinutes(range.max.minute);
            handleChange(time);
        }
        else if (range.min.date && currentTimestamp < range.min.timestamp) {
            time.setHours(range.min.hour);
            time.setMinutes(range.min.minute);
            handleChange(time);
        }
    }, [currentTimestamp, range, handleChange, getEmitInitDate]);
    // * update input value
    useDepsChange(function () {
        if (!isShowTextfieldValue) {
            textFiledValueRef.current = '';
            return;
        }
        var hour = currentHourMinute.hour, minute = currentHourMinute.minute;
        var formattedTime = getFormattedTime({
            hour: hour,
            minute: minute,
            period: currentPeriod,
            periodTexts: periodTexts.input,
        }, isTwelveHourSystem);
        textFiledValueRef.current = formattedTime;
    }, [
        currentHourMinute.hour,
        currentHourMinute.minute,
        currentTimestamp,
        isTwelveHourSystem,
        currentPeriod,
        periodTexts.input.AM,
        periodTexts.input.PM,
    ]);
    // * when no value, open menu use min as value
    var originalHourValue = isHaveValue
        ? currentHourMinute.hour
        : // * use original range to calculate period value
            range.min.hour || 0;
    // * get boundary must use current show value
    var boundary = useMemo(function () {
        return getNumberPickerBoundary(originalHourValue, {
            range: range,
            isTwelveHourSystem: isTwelveHourSystem,
        });
    }, [isTwelveHourSystem, originalHourValue, range]);
    // * when min and max is in same period
    var toggleTextDisabled = useMemo(function () {
        return !!(range.min.hour &&
            // * min is pm time
            (range.min.hour >= HALF_DAY_HOURS ||
                (range.max.hour &&
                    // * min and max in same AM period
                    range.min.hour < HALF_DAY_HOURS &&
                    range.max.hour < HALF_DAY_HOURS)));
    }, [range.max.hour, range.min.hour]);
    var hourValue = isTwelveHourSystem
        ? originalHourValue % HALF_DAY_HOURS
        : originalHourValue;
    // * when no value, open menu use min as value
    var minuteValue = isHaveValue
        ? currentHourMinute.minute
        : boundary.minute.min;
    var periodValue = isHaveValue
        ? currentPeriod
        : getPeriod(originalHourValue);
    return (React.createElement(PickerTextField, __assign({ ref: ref, action: actionRef, onClear: handleClear, value: textFiledValueRef.current, disabled: disabled, PopoverProps: PopoverProps, ActionSymbol: TimeBorderIcon }, rest),
        React.createElement(StyledPickerPopperWrap, { size: size }, selectionShowType === 'none' ? (React.createElement(React.Fragment, null,
            React.createElement(NumberPicker, __assign({ ref: hourRef, size: size, onInnerChange: handleHourInnerChange, onClick: setHourSelectionShow, onClose: closeMenu, value: hourValue }, boundary.hour, { onUpdateValue: handleHourChange, step: 1, source: isTwelveHourSystem
                    ? twelveHourSystemSource
                    : twentyFourHourSystemSource, renderValue: renderHourValue, automationId: "time-picker-hour", autoFocus: true }, HourPickerProps)),
            React.createElement(RcBox, { textAlign: "center", width: "48px" }, ":"),
            React.createElement(NumberPicker, __assign({ ref: minuteRef, size: size }, boundary.minute, { onUpdateValue: handleMinuteChange, onClick: setMinuteSelectionShow, onClose: closeMenu, value: minuteValue, source: minuteSource, step: TIME_GAP, automationId: "time-picker-minute" }, MinutePickerProps)),
            isTwelveHourSystem && (React.createElement("div", null,
                React.createElement(ToggleText, __assign({ periodTexts: periodTexts.toggle, ref: periodRef, size: size, disabled: toggleTextDisabled, onUpdateValue: onTogglePeriod, onInnerChange: handlePeriodInnerChange, onClose: closeMenu, value: periodValue, "data-focusable": true }, PeriodToggleProps)))))) : (React.createElement(SelectionView, __assign({ size: size }, (isHourView ? boundary.hour : boundary.minute), { source: isHourView
                ? isTwelveHourSystem
                    ? twelveHourSystemSource
                    : twentyFourHourSystemSource
                : minuteSource, isTwelveHourSystem: isHourView && isTwelveHourSystem, value: isHourView ? hourValue : minuteValue, onClick: handleSelectionViewClick, automationId: "time-picker-" + selectionShowType }))))));
});
/** @release */
var RcTimePicker = styled(_RcTimePicker)(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""])));
RcTimePicker.defaultProps = {
    clearBtn: true,
    dateMode: false,
    size: 'medium',
    HourPickerProps: {
        getScreenReaderLabel: function (hour) {
            return "Hour: " + hour + ", use up and down arrow keys to change hour time by 1";
        },
    },
    MinutePickerProps: {
        getScreenReaderLabel: function (minute) {
            return "Minute: " + minute + ", use up and down arrow keys to change minutes time by " + TIME_GAP + " minutes";
        },
    },
    PeriodToggleProps: {
        getScreenReaderLabel: function (period) {
            return period + ", use up or down arrow keys to switch between AM or PM";
        },
    },
};
RcTimePicker.displayName = 'RcTimePicker';
/**
 * when `dateMode={true}` that onChange will return `Date` object,
 * otherwise return timestamp `number`
 */
var ExportType = RcTimePicker;
export { ExportType as RcTimePicker };
var templateObject_1;
